import {
  PdfDocument,
  PdfPage,
  PdfView,
  PdfText,
  PdfLink,
  PdfImage,
  PdfFont,
  PdfStyleSheet,
  PdfSvg,
  PdfPolyline,
} from '@carboledger/ui';
import { REGEX } from '@/config/regex.constants';
import isccLogo from '@/assets/iscc-logo.png';
import { buildDeclarationDocument } from '../constants/declarationDocumentFields';

// The ISCC Proof of Sustainability V4.0 form — its own palette, not the app's.
const POS = Object.freeze({
  band: '#005FAC',
  fill: '#91CCFC',
  box: '#FFFFFF',
  stroke: '#000000',
  divider: '#1F1E1E',
  text: '#000000',
  bandText: '#FFFFFF',
  muted: '#BFBFBF',
  placeholder: '#D9D9D9',
  link: '#015FAF',
  warning: '#F43131',
});

// Every coordinate below is Figma px on the design's 1188.59 px-wide A4 frame (node 7694:61379).
const SCALE = 595.28 / 1188.5867;
const px = (value) => value * SCALE;

// Arial's "normal" leading, which Figma rounds to whole px per size; Helvetica shares its metrics.
const LINE_HEIGHT = 1.15;
const lineHeight = (size) => Math.round(size * LINE_HEIGHT);
// Figma seats Arial's baseline this much (× size) lower in the line box than react-pdf seats Helvetica's.
const BASELINE_SHIFT = 0.055;
// Figma's Arial sets ~0.3% wider than Helvetica; without it a line can keep a word the design wraps.
const TRACKING = 0.0012;
const SUPERSCRIPT_RATIO = 0.645;
const BODY = 19;
const HEADING = 27;
const VALUE_MIN = 9;
const VALUE_PADDING = 6;
const GLYPH_WIDTH = 0.6;

const FRAME = Object.freeze({ x: 48, y: 35, width: 1093 });
const BAND_HEIGHT = 45;
const BAND_STROKE = 4;
const ROW_STROKE = 3;
const CHECKBOX = 19.2;
const ISCC_URL = 'www.iscc-system.org';
const ISCC_HREF = 'https://www.iscc-system.org/';

// Figma wraps whole words; react-pdf would otherwise hyphenate them ("mate-rial").
PdfFont.registerHyphenationCallback((word) => [word]);

const styles = PdfStyleSheet.create({
  page: { padding: 0, backgroundColor: POS.box, fontFamily: 'Helvetica', color: POS.text },
  regular: { fontFamily: 'Helvetica' },
  bold: { fontFamily: 'Helvetica-Bold' },
  superscript: { verticalAlign: 'super' },
  link: { color: POS.link, textDecoration: 'underline' },
  box: {
    position: 'absolute',
    backgroundColor: POS.box,
    justifyContent: 'center',
    paddingHorizontal: px(VALUE_PADDING),
  },
  checkbox: {
    position: 'absolute',
    width: px(CHECKBOX),
    height: px(CHECKBOX),
    backgroundColor: POS.box,
    borderWidth: px(1.2),
    borderColor: POS.stroke,
    alignItems: 'center',
    justifyContent: 'center',
  },
  logo: { position: 'absolute', overflow: 'hidden' },
});

const at = (x, y, width, height) => ({
  position: 'absolute',
  left: px(x),
  top: px(y),
  width: width === undefined ? undefined : px(width),
  height: height === undefined ? undefined : px(height),
});

// A label vertically centred in a taller Figma text box, as `justify-center` lays it out.
const centred = (y, boxHeight, lines = 1, size = BODY) =>
  y + (boxHeight - lines * lineHeight(size)) / 2;

// No PDF autofit: step the size down until the value's estimated lines fit its box.
const fitSize = (value, width, height) => {
  const length = String(value ?? '').length;
  for (let size = BODY; size > VALUE_MIN; size -= 1) {
    const perLine = Math.max(1, Math.floor((width - 2 * VALUE_PADDING) / (size * GLYPH_WIDTH)));
    if (Math.ceil(length / perLine) * size * LINE_HEIGHT <= height) return size;
  }
  return VALUE_MIN;
};

/**
 * Props:
 *   x, y      the Figma text node's top-left
 *   width     the wrap width; omitted, the copy carries the design's own line breaks
 *   size      the font size in Figma px
 *   bold      Arial Bold on the design
 *   color     the text colour
 *   style     extra text styles
 *   children  the label; `^n^` marks a footnote superscript
 */
const Label = ({ x, y, width, size = BODY, bold = false, color = POS.text, style, children }) => {
  const parts =
    typeof children === 'string' ? children.split(REGEX.SUPERSCRIPT_MARKER) : [children];
  return (
    <PdfText
      style={[
        at(x, y + size * BASELINE_SHIFT, width ?? FRAME.width),
        bold ? styles.bold : styles.regular,
        {
          fontSize: px(size),
          lineHeight: lineHeight(size) / size,
          letterSpacing: px(size * TRACKING),
          color,
        },
        style,
      ]}
    >
      {parts.map((part, index) =>
        index % 2 ? (
          <PdfText
            key={part}
            style={[styles.superscript, { fontSize: px(size * SUPERSCRIPT_RATIO) }]}
          >
            {part}
          </PdfText>
        ) : (
          part
        ),
      )}
    </PdfText>
  );
};

/**
 * Props:
 *   x, y, width, height  the white box's bounds
 *   value                the text it holds
 */
const ValueBox = ({ x, y, width, height, value }) => {
  const size = fitSize(value, width, height);
  return (
    <PdfView style={[styles.box, at(x, y, width, height)]}>
      <PdfText style={{ fontSize: px(size), lineHeight: LINE_HEIGHT }}>{value}</PdfText>
    </PdfView>
  );
};

/**
 * Props:
 *   x, y     the box's top-left
 *   checked  draws the tick
 */
// A glyph won't do: the standard PDF fonts are WinAnsi and have no U+2713.
const TickBox = ({ x, y, checked }) => (
  <PdfView style={[styles.checkbox, { left: px(x), top: px(y) }]}>
    {checked ? (
      <PdfSvg width={px(14)} height={px(14)} viewBox="0 0 10 10">
        <PdfPolyline points="1,5 4,8 9,2" stroke={POS.text} strokeWidth={1.6} fill="none" />
      </PdfSvg>
    ) : null}
  </PdfView>
);

/**
 * Props:
 *   t       the translator, for the two answers
 *   x       the Yes box's left edge
 *   y       the row's top
 *   answer  `{ yes, no }`, which box is ticked
 */
const YesNo = ({ t, x, y, answer }) => (
  <>
    <TickBox x={x} y={y} checked={answer.yes} />
    <Label x={x + 30.2} y={y}>
      {t('COMMON_YES')}
    </Label>
    <TickBox x={x + 103.8} y={y} checked={answer.no} />
    <Label x={x + 134} y={y}>
      {t('COMMON_NO')}
    </Label>
  </>
);

/**
 * Props:
 *   t        the translator
 *   x, y     the box's top-left
 *   checked  ticks the box
 *   gap      the caption's offset from the box's left edge
 */
const YesBox = ({ t, x, y, checked, gap = 21.2 }) => (
  <>
    <TickBox x={x} y={y} checked={checked} />
    <Label x={x + gap} y={y}>
      {t('COMMON_YES')}
    </Label>
  </>
);

/**
 * Props:
 *   y       the band's top
 *   number  the section number, set in the list-marker gutter
 *   title   the heading
 */
const Band = ({ y, number, title }) => (
  <>
    <PdfView
      style={[
        at(FRAME.x, y, FRAME.width, BAND_HEIGHT),
        { backgroundColor: POS.band, borderWidth: px(BAND_STROKE), borderColor: POS.stroke },
      ]}
    />
    {number ? (
      <Label x={62.5} y={y + 7} size={HEADING} bold color={POS.bandText}>
        {number}
      </Label>
    ) : null}
    <Label x={number ? 95.5 : 55} y={y + 7} size={HEADING} bold color={POS.bandText}>
      {title}
    </Label>
  </>
);

/**
 * Props:
 *   y       the row's top
 *   height  the row's height, its bottom rule included
 *   top     also rules the row's top edge
 */
const Row = ({ y, height, top = false }) => (
  <PdfView
    style={[
      at(FRAME.x, y, FRAME.width, height),
      {
        backgroundColor: POS.fill,
        borderBottomWidth: px(ROW_STROKE),
        borderTopWidth: top ? px(ROW_STROKE) : 0,
        borderColor: POS.stroke,
      },
    ]}
  />
);

/**
 * Props:
 *   height  the frame's height
 *   stroke  draws the outer rule instead of the fill
 */
const Frame = ({ height, stroke = false }) => (
  <PdfView
    style={[
      at(FRAME.x, FRAME.y, FRAME.width, height),
      stroke
        ? { borderWidth: px(BAND_STROKE), borderColor: POS.stroke }
        : { backgroundColor: POS.fill },
    ]}
  />
);

/**
 * Props:
 *   t    the translator
 *   doc  buildDeclarationDocument's output
 */
const PageOne = ({ t, doc }) => (
  <PdfPage size="A4" style={styles.page}>
    <Frame height={1612} />
    <PdfView style={[at(FRAME.x, FRAME.y, FRAME.width, 81), { backgroundColor: POS.band }]} />
    <Label x={55} y={44} size={HEADING} bold color={POS.bandText}>
      {t('ALLOCATIONS_POS_TITLE')}
    </Label>
    <Label x={55} y={82} size={HEADING} bold color={POS.muted}>
      {t('ALLOCATIONS_POS_SUBTITLE')}
    </Label>
    <Label x={1080} y={44} size={HEADING} color={POS.muted}>
      {t('ALLOCATIONS_POS_VERSION')}
    </Label>

    <Band y={261} />
    <Band y={846} number="1." title={t('DECLARATIONS_VALIDATE_SUMMARY_GENERAL_INFO')} />
    <Band y={1375} number="2." title={t('SECTION_CERTIFICATION_SCOPE')} />

    <Label x={55} y={143}>
      {t('ALLOCATIONS_POS_NUMBER')}
    </Label>
    <ValueBox x={375} y={125} width={419} height={58} value={doc.posNumber} />
    <Label x={55} y={209}>
      {t('ALLOCATIONS_POS_ISSUANCE')}
    </Label>
    <ValueBox x={375} y={191} width={419} height={58} value={doc.dateOfIssuance} />

    <PdfView style={[at(834, 143, 121, 71), { backgroundColor: POS.placeholder }]} />
    <PdfView style={[styles.logo, at(982, 143, 121, 71)]}>
      <PdfImage src={isccLogo} style={at(0, -22.63, 121, 120.39)} />
    </PdfView>
    <Label x={881} y={227}>
      <PdfLink src={ISCC_HREF} style={styles.link}>
        {ISCC_URL}
      </PdfLink>
    </Label>

    <Label x={55} y={268} size={HEADING} bold color={POS.bandText}>
      {t('SECTION_SUPPLIER')}
    </Label>
    <Label x={622} y={268} size={HEADING} bold color={POS.bandText}>
      {t('SECTION_RECIPIENT')}
    </Label>
    <PdfView style={[at(594, 261, 4, 324), { backgroundColor: POS.divider }]} />

    <Label x={81} y={308}>
      {t('ALLOCATIONS_POS_NAME')}
    </Label>
    <ValueBox x={81} y={332} width={492} height={54} value={doc.supplier.name} />
    <Label x={81} y={394}>
      {t('ALLOCATIONS_POS_ADDRESS')}
    </Label>
    <ValueBox x={81} y={418} width={492} height={83} value={doc.supplier.address} />
    <Label x={81} y={503}>
      {`${t('ALLOCATIONS_POS_CERTIFICATION_SYSTEM')} `}
      <PdfText style={styles.bold}>{t('HOME_MODULE_ISCC_EU')}</PdfText>
    </Label>
    <Label x={81} y={533}>
      {t('ALLOCATIONS_POS_CERTIFICATE_NUMBER')}
    </Label>
    <ValueBox x={81} y={557} width={492} height={28} value={doc.supplier.certificateNumber} />

    <Label x={622} y={308}>
      {t('ALLOCATIONS_POS_NAME')}
    </Label>
    <ValueBox x={622} y={332} width={492} height={54} value={doc.recipient.name} />
    <Label x={622} y={394}>
      {t('ALLOCATIONS_POS_ADDRESS')}
    </Label>
    <ValueBox x={622} y={418} width={492} height={107} value={doc.recipient.address} />
    <Label x={622} y={533}>
      {t('ALLOCATIONS_POS_CONTRACT_NUMBER')}
    </Label>
    <ValueBox x={622} y={557} width={492} height={28} value={doc.recipient.contractNumber} />

    <Label x={55} y={604}>
      {t('ALLOCATIONS_POS_DISPATCH_ADDRESS')}
    </Label>
    <ValueBox x={358} y={604} width={756} height={46} value={doc.dispatchAddress} />
    <TickBox x={368} y={659} checked={doc.dispatchSameAsSupplier} />
    <Label x={389.2} y={659}>
      {t('ALLOCATIONS_FIELD_SAME_AS_SUPPLIER')}
    </Label>
    <Label x={55} y={697}>
      {t('ALLOCATIONS_POS_RECEIPT_ADDRESS')}
    </Label>
    <ValueBox x={358} y={697} width={756} height={46} value={doc.receiptAddress} />
    <TickBox x={368} y={752} checked={doc.receiptSameAsRecipient} />
    <Label x={389.2} y={752}>
      {t('ALLOCATIONS_FIELD_SAME_AS_RECIPIENT')}
    </Label>
    <Label x={55} y={centred(798, 30, 2)}>
      {t('ALLOCATIONS_POS_DISPATCH_DATE')}
    </Label>
    <ValueBox x={358} y={790} width={376} height={46} value={doc.dateOfDispatch} />

    <Label x={81} y={centred(899, 30)}>
      {t('ALLOCATIONS_POS_TYPE_OF_PRODUCT')}
    </Label>
    <ValueBox x={384} y={900} width={719} height={28} value={doc.typeOfProduct} />
    <Label x={81} y={centred(938, 30)}>
      {t('ALLOCATIONS_POS_TYPE_OF_RAW_MATERIAL')}
    </Label>
    <ValueBox x={384} y={939} width={719} height={28} value={doc.rawMaterialType} />
    <Label x={81} y={centred(977, 30)}>
      {t('ALLOCATIONS_POS_ADDITIONAL_INFORMATION')}
    </Label>
    <ValueBox x={384} y={978} width={719} height={28} value={doc.additionalInformation} />
    <Label x={81} y={centred(1024, 30, 2)}>
      {t('ALLOCATIONS_POS_COUNTRY_OF_ORIGIN')}
    </Label>
    <ValueBox x={384} y={1016} width={719} height={46} value={doc.countryOfOrigin} />

    <Label x={81} y={1074}>
      {t('ALLOCATIONS_POS_QUANTITY')}
    </Label>
    <ValueBox x={384} y={1071} width={226} height={28} value={doc.quantity} />
    <Label x={621} y={1074}>
      {t('ALLOCATIONS_POS_UNIT_CUBIC_METERS_15C')}
    </Label>
    <TickBox x={723} y={1074} checked={doc.unitCubicMeters} />
    <Label x={744.2} y={1074}>
      {t('ALLOCATIONS_POS_UNIT_CUBIC_METERS')}
    </Label>
    <TickBox x={834} y={1074} checked={doc.unitMetricTons} />
    <Label x={855.2} y={1074}>
      {t('ALLOCATIONS_POS_UNIT_METRIC_TONS')}
    </Label>
    <Label x={81} y={1111}>
      {t('ALLOCATIONS_POS_ENERGY_CONTENT')}
    </Label>
    <ValueBox x={384} y={1108} width={226} height={28} value={doc.energyContent} />
    <Label x={621} y={1111}>
      {t('ALLOCATIONS_POS_UNIT_MJ')}
    </Label>

    <Label x={81} y={1145}>
      {t('ALLOCATIONS_POS_EU_RED_COMPLIANT')}
    </Label>
    <YesBox t={t} x={394} y={1145} checked={doc.euRedCompliant} />
    <Label x={81} y={1176}>
      {t('ALLOCATIONS_POS_ISCC_COMPLIANT')}
    </Label>
    <YesBox t={t} x={394} y={1176} checked={doc.isccCompliant} />
    <Label x={81} y={1210}>
      {t('ALLOCATIONS_POS_CHAIN_OF_CUSTODY')}
    </Label>
    <ValueBox x={397} y={1207} width={226} height={28} value={doc.chainOfCustody} />
    <Label x={81} y={1250.5}>
      {t('ALLOCATIONS_POS_COUNTRY_OF_BIOFUEL')}
    </Label>
    <ValueBox x={757} y={1244} width={164} height={35} value={doc.countryOfBiofuelProduction} />
    <Label x={81} y={1294.5}>
      {t('ALLOCATIONS_POS_START_DATE_BIOFUEL')}
    </Label>
    <ValueBox x={757} y={1288} width={164} height={35} value={doc.startDateOfBiofuelProduction} />
    <Label x={81} y={1338} size={20}>
      {t('ALLOCATIONS_POS_START_DATE_BIOLIQUID')}
    </Label>
    <ValueBox x={757} y={1332} width={164} height={35} value={doc.startDateOfBioliquidUse} />

    <Row y={1420} height={42} />
    <Label x={62} y={1430}>
      {t('ALLOCATIONS_POS_SUSTAINABILITY_CRITERIA')}
    </Label>
    <YesNo t={t} x={968} y={1430} answer={doc.sustainabilityCriteria} />
    <Row y={1462} height={42} />
    <Label x={62} y={1472}>
      {t('ALLOCATIONS_POS_INTERMEDIATE_CROP')}
    </Label>
    <YesNo t={t} x={968} y={1472} answer={doc.intermediateCrop} />
    <Row y={1504} height={42} />
    <Label x={62} y={1514}>
      {t('ALLOCATIONS_POS_LOW_ILUC_RISK')}
    </Label>
    <YesNo t={t} x={968} y={1514} answer={doc.lowIlucRisk} />
    <Row y={1546} height={101} />
    <Label x={62} y={1556}>
      {t('ALLOCATIONS_POS_WASTE_OR_RESIDUE')}
    </Label>
    <YesNo t={t} x={968} y={1556} answer={doc.wasteOrResidue} />
    <Label x={62} y={1592} width={490}>
      {t('FORM_WASTE_OR_BY_PRODUCT_PERMIT_NUMBER')}
    </Label>
    <ValueBox x={619} y={1592} width={460} height={44} value={doc.wastePermitNumber} />

    <Frame height={1612} stroke />
  </PdfPage>
);

const BREAKDOWN = Object.freeze([
  { key: 'eec', term: 'ALLOCATIONS_POS_TERM_EEC', termX: 129.6, boxX: 111, op: '+', opX: 196 },
  { key: 'el', term: 'ALLOCATIONS_POS_TERM_EL', termX: 244.6, boxX: 216, op: '+', opX: 299 },
  { key: 'ep', term: 'ALLOCATIONS_POS_TERM_EP', termX: 343.7, boxX: 319, op: '+', opX: 402 },
  { key: 'etd', term: 'ALLOCATIONS_POS_TERM_ETD', termX: 444.9, boxX: 422, op: '+', opX: 505 },
  { key: 'eu', term: 'ALLOCATIONS_POS_TERM_EU', termX: 550, boxX: 525, op: '-', opX: 611 },
  { key: 'esca', term: 'ALLOCATIONS_POS_TERM_ESCA', termX: 645.4, boxX: 628, op: '-', opX: 715.8 },
  {
    key: 'eccs',
    term: 'ALLOCATIONS_POS_TERM_ECCS',
    termX: 748.5,
    boxX: 731,
    boxWidth: 76,
    op: '-',
    opX: 818.9,
  },
  { key: 'eccr', term: 'ALLOCATIONS_POS_TERM_ECCR', termX: 853.6, boxX: 835 },
]);

const LEGEND = Object.freeze([
  { term: 'ALLOCATIONS_POS_TERM_EEC', text: 'ALLOCATIONS_POS_LEGEND_EEC', y: 1238 },
  { op: '+', term: 'ALLOCATIONS_POS_TERM_EL', text: 'ALLOCATIONS_POS_LEGEND_EL', y: 1277 },
  { op: '+', term: 'ALLOCATIONS_POS_TERM_EP', text: 'ALLOCATIONS_POS_LEGEND_EP', y: 1316 },
  {
    op: '+',
    term: 'ALLOCATIONS_POS_TERM_ETD',
    text: 'ALLOCATIONS_POS_LEGEND_ETD',
    y: 1362,
    textY: 1351,
  },
  { op: '+', term: 'ALLOCATIONS_POS_TERM_EU', text: 'ALLOCATIONS_POS_LEGEND_EU', y: 1408 },
  { op: '-', term: 'ALLOCATIONS_POS_TERM_ESCA', text: 'ALLOCATIONS_POS_LEGEND_ESCA', y: 1447 },
  { op: '-', term: 'ALLOCATIONS_POS_TERM_ECCS', text: 'ALLOCATIONS_POS_LEGEND_ECCS', y: 1486 },
  { op: '-', term: 'ALLOCATIONS_POS_TERM_ECCR', text: 'ALLOCATIONS_POS_LEGEND_ECCR', y: 1525 },
  { op: '=', term: 'ALLOCATIONS_POS_TERM_E', text: 'ALLOCATIONS_POS_LEGEND_E', y: 1564 },
]);

const SAVINGS = Object.freeze([
  {
    key: 'electricity',
    label: 'FORM_BIOLIQUIDS_ELECTRICITY_SAVING',
    x: 96,
    y: 609,
    lines: 2,
    width: 429,
  },
  {
    key: 'electricityOutermost',
    label: 'FORM_BIOMASS_OUTERMOST_REGIONS_SAVING',
    x: 600,
    y: 609,
    lines: 2,
    width: 415,
  },
  { key: 'usefulHeat', label: 'ALLOCATIONS_POS_SAVING_USEFUL_HEAT', x: 96, y: 669, lines: 3 },
  {
    key: 'heatSubstitution',
    label: 'ALLOCATIONS_POS_SAVING_SUBSTITUTION',
    x: 600,
    y: 669,
    lines: 3,
  },
]);

const ADDONS = Object.freeze([
  { key: 'foodSecurityStandard', label: 'ALLOCATIONS_POS_ADDON_FOOD_SECURITY', y: 932 },
  { key: 'euDeforestationRegulation', label: 'ALLOCATIONS_POS_ADDON_DEFORESTATION', y: 971 },
  { key: 'nonGmoFoodFeed', label: 'ALLOCATIONS_POS_ADDON_NON_GMO_FOOD_FEED', y: 1010 },
  { key: 'nonGmoTechnicalMarkets', label: 'ALLOCATIONS_POS_ADDON_NON_GMO_TECHNICAL', y: 1049 },
]);

/**
 * Props:
 *   t    the translator
 *   doc  buildDeclarationDocument's output
 */
const PageTwo = ({ t, doc }) => (
  <PdfPage size="A4" style={styles.page}>
    <Frame height={1612} />
    <Band y={117} number="3." title={t('ALLOCATIONS_POS_SECTION_GHG')} />
    <Band y={869} number="4." title={t('SECTION_VOLUNTARY_ADD_ONS')} />
    <Band y={1164} number="5." title={t('ALLOCATIONS_POS_SECTION_EXPLANATIONS')} />

    <Row y={35} height={88} />
    <Label x={62} y={45}>
      {t('ALLOCATIONS_POS_SUPPORT_RECEIVED')}
    </Label>
    <YesNo t={t} x={968} y={45} answer={doc.supportReceived} />
    <Label x={62} y={81}>
      {t('FORM_SUPPORT_NATURE_AND_SCHEME')}
    </Label>
    <ValueBox x={619} y={78} width={460} height={28} value={doc.supportNatureAndScheme} />

    <Label x={95} y={179}>
      {t('ALLOCATIONS_POS_RED_III_TOTAL_VALUE')}
    </Label>
    <YesNo t={t} x={863} y={179} answer={doc.redIiiTotalValue} />
    <Label x={71.5} y={210}>
      {t('ALLOCATIONS_POS_E_EQUALS')}
    </Label>
    <Label x={111.5} y={210}>
      {t('ALLOCATIONS_POS_TOTAL_GHG_EMISSIONS')}
    </Label>
    <ValueBox x={863.5} y={207} width={75} height={28} value={doc.totalGhgEmissions} />
    <Label x={949.5} y={207}>
      {t('ALLOCATIONS_POS_UNIT_GCO2EQ_MJ')}
    </Label>

    {doc.showBreakdown ? (
      <>
        <Label x={71.5} y={241}>
          {t('ALLOCATIONS_POS_E_EQUALS')}
        </Label>
        {BREAKDOWN.map((term) => (
          <PdfView key={term.key}>
            <Label x={term.termX} y={241}>
              {t(term.term)}
            </Label>
            <ValueBox
              x={term.boxX}
              y={272}
              width={term.boxWidth ?? 75}
              height={28}
              value={doc.breakdown[term.key]}
            />
            {term.op ? (
              <Label x={term.opX} y={272}>
                {term.op}
              </Label>
            ) : null}
          </PdfView>
        ))}
        <Label x={919} y={272} bold>
          =
        </Label>
        <Label x={1031} y={274} size={15} bold>
          {t('ALLOCATIONS_POS_UNIT_GCO2_EQ_MJ')}
        </Label>
      </>
    ) : null}

    <Label x={161} y={314}>
      {t('ALLOCATIONS_POS_ALLOCATED_HEAT')}
    </Label>
    <ValueBox x={319} y={311} width={75} height={28} value={doc.allocatedHeat} />
    <Label x={405} y={314}>
      {t('ALLOCATIONS_POS_UNIT_HEAT')}
    </Label>
    <Label x={634.7} y={314}>
      {t('ALLOCATIONS_POS_ALLOCATED_ELECTRICITY')}
    </Label>
    <ValueBox x={835.7} y={311} width={75} height={28} value={doc.allocatedElectricity} />
    <Label x={921.7} y={314}>
      {t('ALLOCATIONS_POS_UNIT_ELECTRICITY')}
    </Label>

    <Row y={354} height={76} top />
    <Label x={62} y={381}>
      {t('ALLOCATIONS_POS_ESCA_BONUS_CAP')}
    </Label>
    <ValueBox x={717} y={371} width={410} height={42} value={doc.escaBonusCap} />
    <Row y={431} height={76} />
    <Label x={62} y={458}>
      {t('ALLOCATIONS_POS_NUTS2_AREA')}
    </Label>
    <ValueBox x={717} y={448} width={410} height={42} value={doc.nuts2Area} />

    <Row y={507} height={254} />
    <Label x={96} y={533} bold>
      {t('ALLOCATIONS_POS_SAVING_TITLE')}
    </Label>
    <Label x={96} y={571} bold>
      {doc.saving.transport}
    </Label>
    <Label x={160} y={571}>
      {t('FIELD_BIOFUELS_TRANSPORT_SAVING')}
    </Label>
    {SAVINGS.map((saving) => (
      <PdfView key={saving.key}>
        <Label x={saving.x} y={centred(saving.y, saving.lines * 22)} bold>
          {doc.saving[saving.key]}
        </Label>
        <Label x={saving.x + 64} y={saving.y} width={saving.width}>
          {t(saving.label)}
        </Label>
      </PdfView>
    ))}

    <Label x={96} y={775}>
      {t('ALLOCATIONS_POS_VALIDITY')}
    </Label>

    {ADDONS.map((addon) => (
      <PdfView key={addon.key}>
        <Label x={103} y={centred(addon.y, 30)}>
          {t(addon.label)}
        </Label>
        <YesBox t={t} x={1021.5} y={addon.y + 4} checked={doc.addons[addon.key]} gap={30.2} />
      </PdfView>
    ))}
    <Label x={103} y={centred(1116, 30)} bold color={POS.warning}>
      {t('ALLOCATIONS_POS_NOT_VALID_WITHOUT_ID')}
    </Label>

    {LEGEND.map((entry) => (
      <PdfView key={entry.term}>
        {entry.op ? (
          <Label x={103} y={entry.y}>
            {entry.op}
          </Label>
        ) : null}
        <Label x={124} y={entry.y}>
          {t(entry.term)}
        </Label>
        <Label x={176} y={entry.textY ?? entry.y}>
          {t(entry.text)}
        </Label>
      </PdfView>
    ))}

    <Frame height={1612} stroke />
  </PdfPage>
);

const EXPLANATIONS = Object.freeze([
  ['ALLOCATIONS_POS_EXPLANATION_1'],
  ['ALLOCATIONS_POS_EXPLANATION_2'],
  ['ALLOCATIONS_POS_EXPLANATION_3'],
  ['ALLOCATIONS_POS_EXPLANATION_4'],
  ['ALLOCATIONS_POS_EXPLANATION_5'],
  ['ALLOCATIONS_POS_EXPLANATION_6'],
  ['ALLOCATIONS_POS_EXPLANATION_7'],
  ['ALLOCATIONS_POS_EXPLANATION_8'],
  [
    'ALLOCATIONS_POS_EXPLANATION_9',
    'ALLOCATIONS_POS_SAVING_FORMULA',
    'ALLOCATIONS_POS_SAVING_FORMULA_EB',
    'ALLOCATIONS_POS_SAVING_FORMULA_EF',
  ],
  ['ALLOCATIONS_POS_COMPARATORS'],
  ['ALLOCATIONS_POS_COMPARATOR_TRANSPORT'],
  ['ALLOCATIONS_POS_COMPARATOR_ELECTRICITY'],
  ['ALLOCATIONS_POS_COMPARATOR_OUTERMOST'],
  ['ALLOCATIONS_POS_COMPARATOR_USEFUL_HEAT'],
  ['ALLOCATIONS_POS_COMPARATOR_SUBSTITUTION'],
]);

/**
 * Props:
 *   t  the translator
 */
const PageThree = ({ t }) => (
  <PdfPage size="A4" style={styles.page}>
    <Frame height={1170} />
    <Label x={90} y={74}>
      {EXPLANATIONS.map((lines) => lines.map((key) => t(key)).join('\n')).join('\n\n')}
    </Label>
    <Frame height={1170} stroke />
  </PdfPage>
);

/**
 * Props:
 *   t       the translator, so every string stays in en_labels
 *   values  the Edit declaration form's values
 */
export const DeclarationPdfDocument = ({ t, values = {} }) => {
  const doc = buildDeclarationDocument(t, values);
  return (
    <PdfDocument title={t('ALLOCATIONS_POS_TITLE')}>
      <PageOne t={t} doc={doc} />
      <PageTwo t={t} doc={doc} />
      <PageThree t={t} />
    </PdfDocument>
  );
};

/** The document element for a set of form values — what render/download are handed. */
export const buildDeclarationPdf = (t, values) => <DeclarationPdfDocument t={t} values={values} />;
